import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../commponents/layout/adminLayout";
import DocumentInformation, {
  type DocumentFieldConfig,
} from "../../commponents/document/DocumentInformation";

import QuotationSummary from "../../commponents/document/DocumentSummary";

import { editQuotation, getQuotationById, getQuotationStatus } from "../../api/quotationApi";
import { getSales } from "../../api/salesApi";

import StatusDropdown, { type status } from "../../commponents/StatusDropdown";
import type { ItemRow } from "../../commponents/document/DocumentItemTable";
import DocumentItemTable from "../../commponents/document/DocumentItemTable";

import {
  type FieldErrors,
  type ItemsErrors,
  runFieldValidation,
  requiredText,
  positiveNumber,
  validateEmail,
  validateItems,
  validateNumberRange,
  withErrorClear,
  focusFirstError,
} from "../../helpers/validation";
import { showError, showInfo, showSuccess } from "../../utils/ alert";
import CustomerPicker from "../../commponents/document/CustomerPicker";
import type { CustomerOption } from "../../api/customerApi";
import { getAuthUser } from "../../api/authApi";
import { isForbiddenError } from "../../lib/axios";
import { isSalesEditLocked, isSalesRole } from "../../lib/roleAccess";

interface SalesOption {
  id: number;
  name: string;
}

interface QuotationFormValue {
  recipient_name: string;
  attention: string;
  email: string;
  quotation_no?: string;
  quotation_date: string;
  sales_id: number | "";
  delivery_time: string;
  place_of_delivery: string;
  payment: string;
  validity: string;
}

export default function EditQuotation() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isSales = isSalesRole(getAuthUser()?.role);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [recipientName, setRecipientName] = useState("");
  const [attention, setAttention] = useState("");
  const [email, setEmail] = useState("");
  const [quotationNo, setQuotationNo] = useState("");
  const [quotationDate, setQuotationDate] = useState("");


  const [deliveryTime, setDeliveryTime] = useState("");
  const [placeOfDelivery, setPlaceOfDelivery] = useState("");
  const [payment, setPayment] = useState("");
  const [validity, setValidity] = useState("");

  const [items, setItems] = useState<ItemRow[]>([]);
  const [discount, setDiscount] = useState(0);
  const [ppn, setPpn] = useState(11);
  const [statusOptions, setStatusOptions] = useState<status[]>([]);
  const [quotationStatusId, setQuotationStatusId] = useState<number>(0);
  const [salesId, setSalesId] = useState<number>(0);
  const [salesOptions, setSalesOptions] = useState<SalesOption[]>([]);

  // Optional link to an existing customer record.
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerId, setCustomerId] = useState<number | null>(null);

  // --- validation state -------------------------------------------------
  const [errors, setErrors] = useState<FieldErrors>({});
  const [itemsErrors, setItemsErrors] = useState<ItemsErrors>({});
  const [itemsGeneralError, setItemsGeneralError] = useState<string | undefined>();
  const [summaryErrors, setSummaryErrors] = useState<FieldErrors>({});

  const subtotal = items.reduce((sum, item) => sum + item.qty * item.price, 0);
  const discountAmount = subtotal * (discount / 100);
  const totalAfterDiscount = subtotal - discountAmount;
  const ppnAmount = totalAfterDiscount * (ppn / 100);
  const grandTotal = totalAfterDiscount + ppnAmount;

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        setLoading(true);
        const response = await getQuotationById(Number(id));
        const data = response.data.data ?? response.data;

        // Sales membuka URL edit quotation yang sudah diproses (DO/PO/WO):
        // backend pasti menolak simpan (403), jadi kembalikan ke detail.
        if (isSales && isSalesEditLocked(data.status?.code)) {
          showInfo(
            "Tidak dapat diedit",
            "Quotation sudah diproses lebih lanjut (DO/PO/WO) dan tidak dapat diedit."
          );
          navigate(`/quotation/detail/${id}`, { replace: true });
          return;
        }

        setRecipientName(data.customer.company_name ?? "");
        setAttention(data.customer.customer_name ?? "");
        setEmail(data.customer.email ?? "");
        setQuotationNo(data.quotation_no ?? "");
        setQuotationDate(data.quotation_date ? String(data.quotation_date).slice(0, 10) : "");
        setSalesId(data.sales?.id ?? 0);
        setDeliveryTime(data.delivery_time ?? "");
        setPlaceOfDelivery(data.place_of_delivery ?? "");
        setPayment(data.payment ?? "");
        setValidity(data.validity ?? "");

        setItems(
          (data.items ?? []).map((item: any) => ({
            description: item.description,
            qty: Number(item.qty),
            unit: item.unit,
            price: Number(item.price),
            total: Number(item.total),
            product_id: item.product_id ?? null,
          }))
        );

        setDiscount(Number(data.discount ?? 0));
        setPpn(Number(data.ppn ?? 11));
        setQuotationStatusId(data.status?.id ?? 0);

        if (data.customer) {
          setCustomerId(data.customer.id);
          setCustomerQuery(
            data.customer.company_name
              ? `${data.customer.customer_name} (${data.customer.company_name})`
              : data.customer.customer_name
          );
        }
      } catch (error) {
        if (!isForbiddenError(error)) showError("Failed to load quotation.");
        navigate("/quotation");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchQuotation();
  }, [id]);

  const handleSelectCustomer = (customer: CustomerOption) => {
    setCustomerId(customer.id);
    setCustomerQuery(
      customer.companyName
        ? `${customer.customerName} (${customer.companyName})`
        : customer.customerName
    );
    setRecipientName(customer.companyName ?? customer.customerName);
    setAttention(customer.customerName);
    setEmail(customer.email ?? "");
  };

  const handleClearCustomer = () => {
    setCustomerId(null);
    setCustomerQuery("");
  };

  const handleCancel = () => navigate("/quotation");

  const validate = () => {
    const fieldErrors = runFieldValidation<QuotationFormValue>(
      {
        recipient_name: recipientName,
        attention,
        email,
        quotation_no: quotationNo,
        quotation_date: quotationDate,
        sales_id: salesId,
        delivery_time: deliveryTime,
        place_of_delivery: placeOfDelivery,
        payment,
        validity,
      },
      {
        recipient_name: requiredText("Recipient is required"),
        quotation_date: requiredText("Quotation date is required"),
        sales_id: requiredText("Sales is required"),
        email: validateEmail,
      }
    );

    const { itemsErrors: rowErrors, hasError: itemsHasError, generalError } = validateItems(
      items,
      {
        description: requiredText("Description is required"),
        qty: positiveNumber("Quantity must be greater than 0"),
        unit: requiredText("Unit is required"),
        price: positiveNumber("Price must be greater than 0"),
      }
    );

    const nextSummaryErrors: FieldErrors = {};
    const discountError = validateNumberRange(discount, 0, 100, "Discount must be between 0 and 100");
    const ppnError = validateNumberRange(ppn, 0, 100, "PPN must be between 0 and 100");
    if (discountError) nextSummaryErrors.discount = discountError;
    if (ppnError) nextSummaryErrors.ppn = ppnError;

    setErrors(fieldErrors);
    setItemsErrors(rowErrors);
    setItemsGeneralError(generalError);
    setSummaryErrors(nextSummaryErrors);

    const hasAnyError =
      Object.keys(fieldErrors).length > 0 ||
      itemsHasError ||
      Object.keys(nextSummaryErrors).length > 0;

    if (hasAnyError) {
      focusFirstError({
        fieldErrors,
        itemsHasError,
        itemsSectionId: "quotation-items-section",
        summaryHasError: Object.keys(nextSummaryErrors).length > 0,
        summarySectionId: "quotation-summary-section",
      });
    }

    return !hasAnyError;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);

      await editQuotation(Number(id), {
        quotation_no: quotationNo,
        recipient_name: recipientName,
        attention,
        email,

        sales_id: salesId,
        delivery_time: deliveryTime,
        place_of_delivery: placeOfDelivery,

        payment,
        validity,
        quotation_date: quotationDate,

        discount,
        ppn,
        subtotal,
        grand_total: grandTotal,

        quotation_status_id: quotationStatusId,

        customer_id: customerId,

        items,
      });

      await showSuccess("Quotation updated successfully.");
      navigate("/quotation");
    } catch (error) {
      if (!isForbiddenError(error)) showError("Failed to update quotation.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const statuses = await getQuotationStatus();
        setStatusOptions(statuses.data.quotationStatusList);
      } catch (error) {
        showError(
        "Failed",
        "Failed to Load data."
    );
      }
    };

    loadStatus();
  }, []);

  useEffect(() => {
    const fetchSales = async () => {
      try {
        const response = await getSales({
          page: 1,
          limit: 10,
        });
        setSalesOptions(response.data.salesList ?? []);
      } catch (error) {
        showError(
        "Failed",
        "Failed to Load data."
    );
      }
    };

    fetchSales();
  }, []);

  const quotationFields: DocumentFieldConfig<QuotationFormValue>[] = [
    { key: "recipient_name", label: "To", type: "readonly", column: 1 },
    { key: "attention", label: "Attn", type: "readonly", column: 1 },
    { key: "email", label: "Email", type: "readonly", column: 1 },
    { key: "quotation_no", label: "No Quotation", type: "readonly", column: 1 },
    { key: "quotation_date", label: "Date", type: "date", column: 1 },
    {
      key: "sales_id",
      label: "Sales",
      type: "select",
      column: 2,
      numeric: true,
      placeholderOption: { value: "", label: "Select Sales" },
      options: salesOptions.map((sales) => ({
        value: sales.id,
        label: sales.name,
      })),
    },
    { key: "delivery_time", label: "Delivery Time", type: "text", column: 2 },
    {
      key: "place_of_delivery",
      label: "Place of Delivery",
      type: "text",
      column: 2,
    },
    { key: "payment", label: "Payment", type: "text", column: 2 },
    { key: "validity", label: "Validity", type: "text", column: 2 },
  ];

  const handleFieldChange = <K extends keyof QuotationFormValue>(
    key: K,
    value: QuotationFormValue[K]
  ) => {
    switch (key) {
      case "recipient_name":
        setRecipientName(value as string);
        break;
      case "attention":
        setAttention(value as string);
        break;
      case "email":
        setEmail(value as string);
        break;
      case "quotation_no":
        setQuotationNo(value as string);
        break;
      case "quotation_date":
        setQuotationDate(value as string);
        break;
      case "sales_id":
        setSalesId(value as number);
        break;
      case "delivery_time":
        setDeliveryTime(value as string);
        break;
      case "place_of_delivery":
        setPlaceOfDelivery(value as string);
        break;
      case "payment":
        setPayment(value as string);
        break;
      case "validity":
        setValidity(value as string);
        break;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-6 text-center text-gray-400">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="rounded-3xl bg-white p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit Quotation</h2>
          {/* Status hanya boleh diubah Admin/Staff */}
          {!isSales && (
            <div>
              <label className="mr-3 text-sm text-gray-600">Status</label>
              <StatusDropdown
                options={statusOptions}
                value={quotationStatusId}
                onChange={setQuotationStatusId}
              />
            </div>
          )}
        </div>

        <CustomerPicker
          query={customerQuery}
          onQueryChange={setCustomerQuery}
          onSelectCustomer={handleSelectCustomer}
          selectedCustomerId={customerId}
          onClearSelection={handleClearCustomer}
        />

        <DocumentInformation
          value={{
            recipient_name: recipientName,
            attention,
            email,
            quotation_no: quotationNo,
            quotation_date: quotationDate,
            sales_id: Number(salesId),
            delivery_time: deliveryTime,
            place_of_delivery: placeOfDelivery,
            payment,
            validity,
          }}
          fields={quotationFields}
          errors={errors}
          onChange={withErrorClear(handleFieldChange, setErrors)}
        />

        <div className="my-6 border-t border-gray-100" />

        <div id="quotation-items-section">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">
            Quotation Item
          </h3>
          <DocumentItemTable items={items} onChange={setItems} />
          {itemsGeneralError && (
            <p className="mt-2 text-xs text-red-500">{itemsGeneralError}</p>
          )}
          {Object.keys(itemsErrors).length > 0 && (
            <div className="mt-2 space-y-1">
              {Object.entries(itemsErrors).map(([rowIndex, rowErrors]) => (
                <p key={rowIndex} className="text-xs text-red-500">
                  Row {Number(rowIndex) + 1}: {Object.values(rowErrors).join(", ")}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="my-6 border-t border-gray-100" />

        <div id="quotation-summary-section">
          <QuotationSummary
            items={items}
            discount={discount}
            ppn={ppn}
            onDiscountChange={setDiscount}
            onPpnChange={setPpn}
          />
          {summaryErrors.discount && (
            <p className="mt-1 text-xs text-red-500">{summaryErrors.discount}</p>
          )}
          {summaryErrors.ppn && (
            <p className="mt-1 text-xs text-red-500">{summaryErrors.ppn}</p>
          )}
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            disabled={submitting}
            className="rounded-xl border border-red-200 px-5 py-2.5 font-medium text-red-500 hover:bg-red-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={submitting}
            className="rounded-xl bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
